import React, { useEffect, useRef, useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { StatTile } from '../../gamification/components/StatTile';
import { useReferralCodes } from '../api/useReferralCodes';
import { useCampaignOptions } from '../api/useReferralCampaigns';
import {
  CodeKind,
  CodeSortKey,
  CodeStatus,
  DEFAULT_PAGE_SIZE,
  ReferralCode,
} from '../api/types';
import { formatCents, formatCount, formatDate } from '../format';
import {
  CodeKindBadge,
  CodeStatusBadge,
  DateRange,
  DateRangeFilter,
  EmptyRow,
  TableWrap,
  tdClass,
  tdNumClass,
  thClass,
  thNumClass,
} from './controls';

const selectClass =
  'px-2.5 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-gray-200 text-xs focus:outline-none focus:border-violet-500';

const COLUMNS: { key: CodeSortKey; label: string; numeric?: boolean }[] = [
  { key: 'code', label: 'Code' },
  { key: 'kind', label: 'Kind' },
  { key: 'signups', label: 'Signups', numeric: true },
  { key: 'verified_signups', label: 'Verified', numeric: true },
  { key: 'owed', label: '$ owed', numeric: true },
  { key: 'redeemed', label: 'Redeemed', numeric: true },
  { key: 'flagged', label: 'Flagged', numeric: true },
  { key: 'payout_rate', label: 'Rate', numeric: true },
  { key: 'created_at', label: 'Created' },
];

function SortHeader({
  column,
  sort,
  order,
  onSort,
}: {
  column: { key: CodeSortKey; label: string; numeric?: boolean };
  sort: CodeSortKey;
  order: 'asc' | 'desc';
  onSort: (key: CodeSortKey) => void;
}) {
  const isActive = sort === column.key;
  return (
    <th className={column.numeric ? thNumClass : thClass}>
      <button
        type="button"
        onClick={() => onSort(column.key)}
        className={`hover:text-gray-200 ${isActive ? 'text-violet-300' : ''}`}
      >
        {column.label}
        {isActive && <span className="ml-1">{order === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}

function OwnerCell({ code }: { code: ReferralCode }) {
  if (!code.owner) {
    return (
      <span className="text-xs text-gray-500">
        {code.kind === 'campaign' ? code.ambassador_ref || 'no ambassador tag' : 'unbound'}
      </span>
    );
  }
  return (
    <div className="min-w-0">
      <p className="text-xs text-gray-200 truncate">
        {code.owner.display_name || code.owner.email || code.owner.user_id}
      </p>
      {code.ambassador_ref && <p className="text-xs text-gray-500 truncate">{code.ambassador_ref}</p>}
    </div>
  );
}

/**
 * The referral-codes list — and the programme-wide dashboard that replaces the
 * Bar Outreach Leaderboard: verified signups, $ owed, redemptions and
 * review-flagged counts per code, over a date window, filterable by campaign.
 *
 * Totals are for the CURRENT PAGE (the backend's `page_totals`), which is why
 * they are labelled as such — summing across pages would need a separate query.
 */
export function CodeList({
  env,
  range,
  onRangeChange,
  onOpenCode,
  onCreate,
  onEdit,
  refreshKey = 0,
}: {
  env: Env;
  range: DateRange;
  onRangeChange: (r: DateRange) => void;
  onOpenCode: (code: ReferralCode) => void;
  onCreate: () => void;
  onEdit: (code: ReferralCode) => void;
  /** Bumped by the parent after a create/edit so the list re-reads. */
  refreshKey?: number;
}) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<CodeKind | ''>('');
  const [status, setStatus] = useState<CodeStatus | ''>('');
  const [campaignId, setCampaignId] = useState('');
  const [sort, setSort] = useState<CodeSortKey>('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [offset, setOffset] = useState(0);

  const campaigns = useCampaignOptions(env);
  const { data, loading, error, refetch } = useReferralCodes(env, {
    from: range.from || undefined,
    to: range.to || undefined,
    q: q.trim() || undefined,
    kind: kind || undefined,
    status: status || undefined,
    campaign_id: campaignId || undefined,
    sort,
    order,
    limit: DEFAULT_PAGE_SIZE,
    offset,
  });

  // Re-read after the parent saves a code.
  const lastRefresh = useRef(refreshKey);
  useEffect(() => {
    if (lastRefresh.current !== refreshKey) {
      lastRefresh.current = refreshKey;
      refetch();
    }
  }, [refreshKey, refetch]);

  const onSort = (key: CodeSortKey) => {
    if (key === sort) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setOrder('desc');
    }
    setOffset(0);
  };

  const resetPaging = () => setOffset(0);
  const totals = data?.page_totals;
  const codes = data?.codes ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); resetPaging(); }}
            placeholder="Code or ambassador tag"
            className="px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-gray-200 text-xs w-52 focus:outline-none focus:border-violet-500"
          />
          <select
            value={kind}
            onChange={(e) => { setKind(e.target.value as CodeKind | ''); resetPaging(); }}
            className={selectClass}
            aria-label="Kind"
          >
            <option value="">All kinds</option>
            <option value="personal">personal</option>
            <option value="promoter">promoter</option>
            <option value="campaign">campaign</option>
          </select>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as CodeStatus | ''); resetPaging(); }}
            className={selectClass}
            aria-label="Status"
          >
            <option value="">Any status</option>
            <option value="active">active</option>
            <option value="disabled">disabled</option>
          </select>
          <select
            value={campaignId}
            onChange={(e) => { setCampaignId(e.target.value); resetPaging(); }}
            className={selectClass}
            aria-label="Campaign"
          >
            <option value="">All campaigns</option>
            {(campaigns.data?.campaigns ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.kind === 'personal_default' ? `${c.name} (fallback)` : c.name}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={onCreate}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-500"
        >
          + New code
        </button>
      </div>

      <DateRangeFilter value={range} onChange={(r) => { onRangeChange(r); resetPaging(); }} />

      {error && <ErrorBanner message={error} />}

      {/* Window totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <StatTile
          label="Verified signups"
          value={formatCount(totals?.signups_verified)}
          sublabel={`${formatCount(totals?.signups_total)} counted in total`}
        />
        <StatTile
          label="$ owed"
          value={formatCents(totals?.owed_cents)}
          sublabel={`${formatCents(totals?.owed_verified_cents)} on verified only`}
        />
        <StatTile label="Deals redeemed" value={formatCount(totals?.redeemed_deal_count)} />
        <StatTile
          label="Review-flagged"
          value={formatCount(totals?.review_flagged_count)}
          tone={totals && totals.review_flagged_count > 0 ? 'warn' : 'default'}
          sublabel="excluded from $ owed"
        />
        <StatTile label="Codes" value={formatCount(total)} sublabel="matching the filters" />
      </div>
      <p className="text-xs text-gray-500">
        Totals cover the {codes.length} code{codes.length === 1 ? '' : 's'} on this page over the
        selected window. Codes with no activity in the window still appear, with zeros.
      </p>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <TableWrap>
          <thead className="bg-gray-900/60">
            <tr>
              {COLUMNS.map((c) => (
                <SortHeader key={c.key} column={c} sort={sort} order={order} onSort={onSort} />
              ))}
              <th className={thClass}>Promoter / tag</th>
              <th className={thClass}>Deal</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {codes.length === 0 && (
              <EmptyRow span={12}>No referral codes match these filters.</EmptyRow>
            )}
            {codes.map((c) => (
              <tr key={c.code} className="hover:bg-gray-900/40">
                <td className={tdClass}>
                  <button
                    type="button"
                    onClick={() => onOpenCode(c)}
                    className="font-mono text-sm text-violet-300 hover:text-violet-200"
                  >
                    {c.code}
                  </button>
                  {c.status === 'disabled' && (
                    <div className="mt-1">
                      <CodeStatusBadge status={c.status} />
                    </div>
                  )}
                </td>
                <td className={tdClass}>
                  <CodeKindBadge kind={c.kind} />
                </td>
                <td className={tdNumClass}>{formatCount(c.stats.signups_total)}</td>
                <td className={tdNumClass}>{formatCount(c.stats.signups_verified)}</td>
                <td className={tdNumClass}>{formatCents(c.stats.owed_cents)}</td>
                <td className={tdNumClass}>{formatCount(c.stats.redeemed_deal_count)}</td>
                <td className={tdNumClass}>
                  {c.stats.review_flagged_count > 0 ? (
                    <span className="text-amber-300">{formatCount(c.stats.review_flagged_count)}</span>
                  ) : (
                    '0'
                  )}
                </td>
                <td className={tdNumClass}>{formatCents(c.payout_rate_cents)}</td>
                <td className={tdClass}>
                  <span className="text-xs text-gray-400">{formatDate(c.created_at)}</span>
                </td>
                <td className={tdClass}>
                  <OwnerCell code={c} />
                </td>
                <td className={tdClass}>
                  <span className="text-xs text-gray-400">{c.deal_campaign?.name ?? 'fallback deal'}</span>
                </td>
                <td className={tdClass}>
                  <button
                    type="button"
                    onClick={() => onEdit(c)}
                    className="px-2 py-1 text-xs font-medium rounded-md bg-gray-800 text-gray-200 hover:bg-gray-700"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {/* Pager */}
      {total > DEFAULT_PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>
            {offset + 1}–{Math.min(offset + codes.length, total)} of {formatCount(total)}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - DEFAULT_PAGE_SIZE))}
              className="px-2.5 py-1 rounded-md bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={offset + codes.length >= total}
              onClick={() => setOffset(offset + DEFAULT_PAGE_SIZE)}
              className="px-2.5 py-1 rounded-md bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
